import { useEffect, useRef, useState } from 'react';

/**
 * Animates a number from 0 to the target value when scrolled into view.
 * Supports suffixes like "x", "%", "+".
 *
 * Props:
 *   value: string — e.g. "10x", "39%", "70%"
 *   duration: number — animation duration in ms (default: 2000)
 */
export default function CountUp({ value, duration = 2500 }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState('0');
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Parse number and suffix from value like "10x", "39%", "70%"
    const match = value.match(/^(\d+(?:\.\d+)?)(.*)/);
    if (!match) {
      setDisplay(value);
      return;
    }

    const target = parseFloat(match[1]);
    const suffix = match[2] || '';

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          observer.unobserve(el);

          const start = performance.now();
          const isInteger = Number.isInteger(target);

          const animate = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // Ease out quint — very smooth deceleration
            const eased = 1 - Math.pow(1 - progress, 5);
            const current = eased * target;

            setDisplay(
              (isInteger ? Math.round(current) : current.toFixed(1)) + suffix
            );

            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{display}</span>;
}
